/**
 * LIFE — the Alignment Engine (Compass Sector Fill spec, 18 Oct 2026 §3).
 *
 * "Am I doing the RIGHT work, and KEEPING UP with what's due?" — each SHAPE
 * sector is scored on its own, 0–4:
 *   Direction = LINKED_WORK / ACTIVE_WORK
 *     ACTIVE_WORK = items that are open, or were done within the window
 *     LINKED      = traces up to the sector's Endeavour (Aim, task under an
 *                   Aim, or project) — null when there is no Endeavour
 *   Execution = DONE_DUE / DUE_IN_WINDOW
 *     DUE_IN_WINDOW = items (tasks, project tasks, recurring instances) whose
 *                     due time is inside the window and <= NOW. No due date
 *                     → never due. A date with no time is due when its day
 *                     ends (or as soon as it is done).
 *   Score     = 4 × √(Direction × Execution); either null → null ("—").
 *
 * Each STAGE orb scores over its own window (Today = the day, Aim = rolling 7,
 * Goal = the 4-week block, Endeavour = the season). The Home number is the
 * Aim orb's score. Pure — no DB/React/clock.
 */
import { SECTOR_LABELS, type Sector } from '../domain/olmps';
import { DEFAULT_GAME_SETTINGS, type GameSettings, type PointsLedger } from './gameSettings';

export type AlignmentOrb = 'today' | 'aim' | 'goal' | 'endeavour';
export const ALIGNMENT_ORBS: AlignmentOrb[] = ['today', 'aim', 'goal', 'endeavour'];

/** One unit of committed work, normalised from tasks, project tasks and METIS agenda instances. */
export interface WorkItem {
  id: string;
  sector: Sector;
  /** 'YYYY-MM-DD' local — the day it is due. null = no due date (never "due"). */
  date: string | null;
  /** Epoch ms of the due time when a time is set; absent = date only. */
  dueAt?: number | null;
  done: boolean;
  /** 'YYYY-MM-DD' local — the day it was done, if known (falls back to `date`). */
  doneDate?: string | null;
  /** Traces upward to the Endeavour (Aim, task under an Aim, or project). */
  linked: boolean;
}

export type AlignmentStatus = 'aligned' | 'mostly' | 'drifting' | 'misaligned' | 'neglected' | 'unset';

export const ALIGNMENT_STATUS: Record<AlignmentStatus, { word: string; short: string; segments: number | null }> = {
  aligned: { word: 'Aligned', short: 'Aligned', segments: 0 },
  mostly: { word: 'Mostly aligned', short: 'Mostly', segments: 1 },
  drifting: { word: 'Drifting', short: 'Drifting', segments: 2 },
  misaligned: { word: 'Misaligned', short: 'Misaligned', segments: 3 },
  neglected: { word: 'Neglected', short: 'Neglected', segments: 4 },
  unset: { word: 'Not set up', short: 'Not set up', segments: null },
};

export interface SectorAlignment {
  sector: Sector;
  orb: AlignmentOrb;
  /** 0..4 decimal, null = "—". */
  raw: number | null;
  /** Whole number shown on Home (0..4), null = "—". */
  shown: number | null;
  status: AlignmentStatus;
  /** Segments left of the centre line on the compass (0..4), null = grey. */
  segments: number | null;
  direction: number | null;
  execution: number | null;
  /** Work in the window (active), linked share numerator. */
  active: number;
  linked: number;
  /** Due in the window (<= now) and how many of those were done. */
  due: number;
  done: number;
  /** §3 reason line, e.g. "behind by 3 · 2 not linked to your Endeavour" ('' when nothing to say). */
  reasonLine: string;
  /** ATLAS's plain explanation, e.g. "Economic 3.1: behind by 3 · 2 not linked to your Endeavour." */
  reason: string;
}

export function windowDays(orb: AlignmentOrb, game: GameSettings = DEFAULT_GAME_SETTINGS): number {
  return orb === 'today' ? 1 : game.alignmentWindows[orb];
}

/** Raw → whole number + status (§7 table). */
export function bandOf(raw: number | null): { shown: number | null; status: AlignmentStatus } {
  if (raw == null) return { shown: null, status: 'unset' };
  if (raw >= 3.5) return { shown: 4, status: 'aligned' };
  if (raw >= 2.5) return { shown: 3, status: 'mostly' };
  if (raw >= 1.5) return { shown: 2, status: 'drifting' };
  if (raw >= 0.5) return { shown: 1, status: 'misaligned' };
  return { shown: 0, status: 'neglected' };
}

const MS_DAY = 24 * 3600 * 1000;
const keyOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export interface AlignmentInput {
  items: WorkItem[];
  /** Sectors that have an Endeavour set (others are "Not set up"). */
  hasEndeavour: Record<Sector, boolean>;
  /** Unused since the 18 Oct fill spec (Day orbs no longer count as due work); kept so callers compile. */
  ledger?: PointsLedger;
  now: Date;
  game?: GameSettings;
}

/** §3 reason line: "behind by {behind} · {orphan} not linked to your Endeavour". */
export function reasonLineOf(behind: number, orphan: number): string {
  const parts: string[] = [];
  if (behind > 0) parts.push(`behind by ${behind}`);
  if (orphan > 0) parts.push(`${orphan} not linked to your Endeavour`);
  return parts.join(' · ');
}

/** Score one sector over one orb's window. */
export function scoreSector(sector: Sector, orb: AlignmentOrb, input: AlignmentInput): SectorAlignment {
  const game = input.game ?? DEFAULT_GAME_SETTINGS;
  const span = windowDays(orb, game);
  const nowMs = input.now.getTime();
  const noon = new Date(input.now);
  noon.setHours(12, 0, 0, 0);
  const todayKey = keyOf(noon);
  const startKey = keyOf(new Date(noon.getTime() - (span - 1) * MS_DAY));
  const inWindow = (k: string | null | undefined) => !!k && k >= startKey && k <= todayKey;
  const label = SECTOR_LABELS[sector];

  const work = input.items.filter((w) => w.sector === sector);
  // ACTIVE_WORK: open, or done within the window.
  const activeItems = work.filter((w) => !w.done || inWindow(w.doneDate ?? w.date));
  const active = activeItems.length;
  const linked = activeItems.filter((w) => w.linked).length;
  // DUE_IN_WINDOW: due time inside the window and <= now.
  const dueItems = work.filter((w) => {
    if (!inWindow(w.date)) return false;
    if (w.dueAt != null) return w.dueAt <= nowMs;
    return w.date! < todayKey || w.done;
  });
  const due = dueItems.length;
  const done = dueItems.filter((w) => w.done).length;

  const hasEnd = !!input.hasEndeavour[sector];
  const direction = hasEnd && active > 0 ? linked / active : null;
  const execution = due > 0 ? done / due : null;
  const raw = direction == null || execution == null ? null : 4 * Math.sqrt(direction * execution);
  const { shown, status } = bandOf(raw);

  const reasonLine = reasonLineOf(due - done, hasEnd ? active - linked : 0);
  const why = !hasEnd
    ? 'no Endeavour set yet'
    : active === 0
      ? 'no work in this window yet'
      : due === 0
        ? 'nothing due in this window yet'
        : reasonLine || 'on the line — work done and tied to your Endeavour';
  const reason = `${label} ${raw == null ? '—' : raw.toFixed(1)}: ${why}.`;

  return { sector, orb, raw, shown, status, segments: ALIGNMENT_STATUS[status].segments, direction, execution, active, linked, due, done, reasonLine, reason };
}

/** The spec's name for the one alignment function every screen calls. */
export const computeAlignment = scoreSector;

export type AlignmentBoard = Record<Sector, Record<AlignmentOrb, SectorAlignment>>;

/** Every sector × every orb. Each sector is independent: no cross-sector averaging. */
export function scoreBoard(sectors: Sector[], input: AlignmentInput): AlignmentBoard {
  const out = {} as AlignmentBoard;
  for (const s of sectors) {
    out[s] = {} as Record<AlignmentOrb, SectorAlignment>;
    for (const o of ALIGNMENT_ORBS) out[s][o] = scoreSector(s, o, input);
  }
  return out;
}

/** The Home number for a sector = its Aim orb. */
export const homeScore = (b: AlignmentBoard, s: Sector) => b[s].aim;
